import React, { FC, useCallback, useEffect } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import { AttachmentIcon, CloseIcon, InfoCircle, UploadIcon } from '../../../../assets/icons/index';
import LoaderIcon from '../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { BackendRoute } from '../../../../config/index';
import { useLocale } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import {
  fileWithStorageDisplayName,
  getFileDisplayName,
} from '../../../../utils/file-upload-duplicate-names.util';
import { getTranslation } from '../../../../utils/get-translation.util';
import { FormSchema, subFilesSchema } from '../event-form.validation';
import { useEventFileUploadDuplicates } from '../hooks/use-event-file-upload-duplicates';
import { isDocusignFileLink } from '../utils/resolve-linked-signature-file-links.util';
import { EventDocusignLinkedFilesSection } from './event-docusign-linked-files';
import {
  EventFileDuplicateFeedback,
  EventFileUploadRenameModal,
  EventFileUploadStatusIcon,
} from './event-file-upload-duplicate-ui';

type SubFile = {
  abort?: object | undefined;
  doc?:
    | {
        name?: string | undefined;
        size?: number | undefined;
        type?: string | undefined;
      }
    | File
    | undefined;
  docLink?: string | undefined;
  id?: string | undefined;
  loadProgress?: number | undefined;
};

type FieldPath =
  | `optionalStep.fundraising-round.investmentItems.${number}.files`
  | `optionalStep.share-issuance.issuanceItems.${number}.files`
  | `stepOne.eventDetails.buyback.buyBackItems.${number}.files`
  | `stepOne.eventDetails.grant.grantItems.${number}.files`
  | `stepOne.eventDetails.secondaries.secondariesItems.${number}.files`;

type SubEventFileUploadProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  errors?: Merge<FieldError, FieldErrorsImpl<FormSchema>>;
  field: FieldPath;
  files: SubFile[];
  formData: UseFormGetValues<FormSchema>;
  linkedDocusignFileLinks?: string[];
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  text: string;
};

export const SubEventFileUpload: FC<SubEventFileUploadProps> = ({
  files,
  setFormData,
  formData,
  setError,
  clearErrors,
  errors,
  field,
  text,
  companyId,
  linkedDocusignFileLinks = [],
}) => {
  const { messagesLocale } = useLocale();

  const manualFiles = files.filter(
    (file) => !file.docLink || !isDocusignFileLink(file.docLink, linkedDocusignFileLinks),
  );
  const uploadedFiles = manualFiles.reduce((prev, curr) => (curr.docLink ? prev + 1 : prev), 0);
  const isReachedUploadMaximum = uploadedFiles >= 20;
  const getSubFilesData = useCallback(() => formData(field) || [], [field, formData]);

  const {
    isFileNameDuplicate,
    removeFileToRename,
    renameModal,
    renameModalFolderNames,
    resolveDisplayName,
    validateDropFiles,
  } = useEventFileUploadDuplicates({
    files: manualFiles,
    onRenameInForm: ({ currentName, newName }) => {
      const currentFiles = getSubFilesData();
      setFormData(
        field,
        currentFiles.map((file) =>
          getFileDisplayName(file.doc) === currentName
            ? {
                ...file,
                doc:
                  file.doc instanceof File
                    ? new File([file.doc], newName, { type: file.doc.type })
                    : { ...file.doc, name: newName },
              }
            : file,
        ),
        { shouldValidate: true },
      );
    },
  });

  useEffect(() => {
    clearErrors(field);
  }, [clearErrors, field]);

  useEffect(() => {
    const current = getSubFilesData();
    const manualOnly = current.filter(
      (file) => !file.docLink || !isDocusignFileLink(file.docLink, linkedDocusignFileLinks),
    );

    if (manualOnly.length === current.length) return;

    setFormData(field, manualOnly, { shouldDirty: false, shouldValidate: true });
  }, [field, getSubFilesData, linkedDocusignFileLinks, setFormData]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach(({ file, errors }) => {
          errors.forEach((e) => {
            if (e.code === 'file-invalid-type') {
              setError(field, {
                type: 'manual',
                message: `${messagesLocale['INVALID_FILE_TYPE'].replace('{fileType}', file.name)}`,
              });
            }
          });
        });
        return;
      }

      if (isReachedUploadMaximum) return;
      const existingFiles = getSubFilesData();

      const duplicateFile = validateDropFiles(acceptedFiles, existingFiles);
      if (duplicateFile) {
        setError(field, {
          type: 'manual',
          message: messagesLocale['FILE_EXISTS'],
        });
        return;
      }

      const filesToUpload = acceptedFiles.slice(0, 20 - existingFiles.length);
      filesToUpload.forEach(async (file) => {
        const { callback, abort } = ApiService.uploadFile<string>();
        const id = uuidv4();
        const { error, success } = subFilesSchema.safeParse([
          { doc: file, docLink: '', loadProgress: 1, abort, id },
        ]);

        if (!success) return setError(field, error.issues[0]);

        if (success) clearErrors(field);

        const existingFiles = getSubFilesData();

        setFormData(
          field,
          [...existingFiles, { doc: file, docLink: '', loadProgress: 1, abort, id }],
          { shouldValidate: true },
        );

        const link = await callback(
          { endpoint: BackendRoute.SHARE_CLASSES, routePath: ['file'] },
          ['file', file],
          {
            onUploadProgress: (n) => {
              const currentFiles = getSubFilesData();
              setFormData(
                field,
                currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: n } : f)),
              );
            },
            onAboard: () => {
              const currentFiles = getSubFilesData();
              setFormData(
                field,
                currentFiles.filter((f) => f.id !== id),
              );
            },
          },
        );

        if (!link) return;
        const currentFiles = getSubFilesData();
        setFormData(
          field,
          currentFiles.map((f) =>
            f.id === id
              ? {
                  ...f,
                  loadProgress: 100,
                  docLink: link,
                  doc: fileWithStorageDisplayName(file, link),
                }
              : f,
          ),
        );
      });
    },
    [
      clearErrors,
      field,
      getSubFilesData,
      isReachedUploadMaximum,
      messagesLocale,
      setError,
      setFormData,
      validateDropFiles,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    disabled: isReachedUploadMaximum,
    onDropRejected: () =>
      setError(field, {
        message: getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
        type: 'validate',
      }),
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls'],
      'text/csv': ['.csv'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
  });

  const handleDeleteFile = useCallback(
    (id: string) => {
      const current = (formData(field) || []).find((file) => file.id === id);
      removeFileToRename(getFileDisplayName(current?.doc));

      if (current && !current.docLink) current.abort();

      const currentData = (formData(field) || []).filter((file) => file.id !== id);
      setFormData(field, currentData);
      if (currentData.length === 0) return clearErrors(field);
    },
    [clearErrors, field, formData, removeFileToRename, setFormData],
  );

  return (
    <div className="flex w-full max-w-[418px] flex-col gap-3">
      <EventFileUploadRenameModal
        currentName={renameModal.currentName}
        folderNames={renameModalFolderNames}
        isOpen={renameModal.isOpenRenameModal}
        onClose={renameModal.handleCloseRenameModal}
        onOpenChange={renameModal.renameModalToggler}
        onSuccess={renameModal.handleSuccessRenameModal}
      />
      <input
        {...getInputProps()}
        accept=".pdf, .doc, .docx, .xlsx, .xls, .csv, .png, .jpeg, .jpg"
        className="hidden"
        multiple
        type="file"
      />
      <label
        {...getRootProps()}
        className={twMerge(
          'relative flex items-center gap-4 rounded-lg border-[1px] border-dashed border-gray-200 p-4',
          isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
        )}
      >
        {errors ? <InfoCircle className="size-8" thinIconVariant /> : <AttachmentIcon />}
        <div className="flex w-full flex-col gap-2">
          <div className="flex flex-col gap-1">
            {errors ? (
              <span className="text-sm font-[550] text-fireside-600">
                {errors?.message?.toString()}
              </span>
            ) : (
              <span className="text-sm font-[450] text-gray-700 underline">
                Attach files to this {text}
              </span>
            )}
            <div className="flex flex-col text-label-sm font-[450] text-gray-500">
              <span>doc/docx, xls/xlsx, csv, jpeg/jpg, png or pdf</span>
              <span>
                <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
              </span>
            </div>
          </div>
          {errors && (
            <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
              <UploadIcon />
              <AppFormattedMessage id="UPLOAD_AGAIN" />
            </span>
          )}
        </div>
      </label>
      {(manualFiles?.length || 0) > 0 && (
        <div className="flex w-full flex-col gap-2">
          <span className="truncate pb-2 text-xs font-[450] text-gray-500">
            {uploadedFiles > 1 ? (
              <AppFormattedMessage id="COUNT_FILES_UPLOADED" values={{ count: uploadedFiles }} />
            ) : (
              <AppFormattedMessage id="ONE_FILE_UPLOADED" />
            )}
          </span>
          {manualFiles?.map(({ doc, docLink, loadProgress, id }, i) => (
            <div className="flex flex-col gap-2" key={`${id}_${docLink}`}>
              <div className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25">
                <div className="flex justify-between p-3" key={`${doc?.name}_${i}`}>
                  <div className="flex items-center gap-2 overflow-hidden">
                    <EventFileUploadStatusIcon
                      docLink={docLink}
                      isDuplicate={isFileNameDuplicate(doc, docLink)}
                      loadingIcon={<LoaderIcon className="size-4" />}
                    />
                    <span className="truncate text-xs font-[450] text-gray-700">
                      {resolveDisplayName(doc)}
                    </span>
                  </div>
                  <Button
                    className="w-fit p-1 hover:bg-gray-50"
                    onClick={() => handleDeleteFile(id || '')}
                    styleType="NONE"
                    type="button"
                  >
                    <CloseIcon className="size-2" />
                  </Button>
                </div>
                <div
                  className={twMerge(
                    'h-[2px] shrink-0 bg-brand-700 transition-all duration-700',
                    docLink && 'bg-transparent',
                  )}
                  style={{ width: (loadProgress || (docLink && 100)) + '%' }}
                />
              </div>
              <EventFileDuplicateFeedback
                doc={doc}
                docLink={docLink}
                isDuplicate={isFileNameDuplicate(doc, docLink)}
                onRename={renameModal.handleOpenRenameModal}
              />
            </div>
          ))}
        </div>
      )}
      <EventDocusignLinkedFilesSection companyId={companyId} fileLinks={linkedDocusignFileLinks} />
    </div>
  );
};
